import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { CommonDialog } from './CommonDialog';
import { ExtensionControl } from './ExtensionControl';
import { LoginData } from '../../services/api/types';
import {
  useAuthentication,
  useApi,
  useUser,
  useCase,
  useAgentExtension,
  useLocalStorage,
} from '../../hooks';
import { AgentExtensionDialogDetails } from '../../types/agentExtensionDetails';
import {
  STORAGE_CURRENT_CASE_ID,
  STORAGE_KEY_AGENT_EXTENSION_FROM_API,
} from '../../constants';
import moment from 'moment';

import { createTypedLogger } from '../../utils/typedLogger';

const StyledRoot = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 360,
  backgroundColor: theme.palette.background.paper,
}));

const StyledPaper = styled(Dialog)(() => ({
  width: '100%',
  maxHeight: 530,
}));

const StyledPhoneOuter = styled('div')(() => ({
  background: '#00bf30',
  borderRadius: '50%',
  width: '47px',
  height: '47px',
  margin: '0px auto 15px',
  padding: '5px',
}));

const StyledText = styled('div')(() => ({
  marginBottom: '0',
  padding: '0 16px',
  fontSize: '15px',
}));

export interface ExtensionDialogProps {
  isOpen: boolean;
  children: React.ReactNode;
  agentExtension: string;
  closeExtensionDialog?: () => void;
  requestFor?: string;
}

const logger = createTypedLogger('Connected-Vehicle-FrontEnd');

export const ExtensionDialog = ({
  isOpen,
  children,
  agentExtension,
  closeExtensionDialog,
  requestFor,
}: ExtensionDialogProps) => {
  const { api } = useApi();
  const { saveExtension } = useUser();
  const { agentExtensionLogsData } = useAgentExtension();
  const { withLocalStorage } = useLocalStorage();
  const { auth } = useAuthentication();
  const { caseData } = useCase();

  const [open, setOpen] = useState<boolean>(isOpen);
  const [userEmail, setUserEmail] = useState<string>('');
  const keepMounted = true;
  const [data, setData] = useState<any>({
    extension: agentExtension,
    isvalid: agentExtension ? true : false,
  });
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [props, setProps] = useState<any>({
    extension: agentExtension,
  });
  const [caseId, setCaseId] = useState<string>('');
  const [btnOkData, setBtnOk] = useState<any>({ show: false, text: '' });

  const [agentExtensionLogs, setAgentExtensionLogs] = useState<any>({
    extension: '',
    check_in_time: '',
    check_out_time: '',
    release_time: '',
    first_name: '',
    last_name: '',
    email: '',
    duration: 0,
    modified_by: '',
    modified_date: '',
    can_release: false,
    can_modify: false,
  });

  const [reason, setReason] = useState<string>('NA');

  const [showExtensionDetails, setShowExtensionDetails] =
    useState<AgentExtensionDialogDetails>({
      additionalInfo: <></>,
      title: { show: false, text: '' },
      btnOk: { show: false, text: '' },
      maximumWidth: 'xs',
      isChildComponent: false,
    });

  useEffect(() => {
    setUserEmail(auth.getUserEmail());
  }, [auth]);

  useEffect(() => {
    if (
      caseData &&
      caseData.status !== 0 &&
      Object.keys(caseData?.callDetails).length !== 0
    ) {
      const currentCaseId = caseData?.callDetails?.caseId
        ? caseData?.callDetails?.caseId
        : '';
      setCaseId(currentCaseId);
    } else {
      const currentCaseId = withLocalStorage(STORAGE_CURRENT_CASE_ID);
      setCaseId(currentCaseId);
    }
  }, [caseData, withLocalStorage]);

  const localStorageAgentExtension = localStorage.getItem(
    STORAGE_KEY_AGENT_EXTENSION_FROM_API,
  );

  useEffect(() => {
    const extensionDetailsInput: AgentExtensionDialogDetails = {
      additionalInfo: (
        <p>Please enter your phone extension to start receiving calls.</p>
      ),
      btnOk: { show: true, text: 'OK' },
      maximumWidth: 'xs',
      isChildComponent: true,
    };

    if ((!agentExtension || agentExtension === undefined) && isOpen) {
      // case 2 is covered here
      if (
        agentExtensionLogsData &&
        agentExtensionLogsData.extension &&
        agentExtensionLogsData.check_in_time &&
        agentExtensionLogsData.check_out_time &&
        agentExtensionLogsData.release_time
      ) {
        const currentDateTime = moment(new Date()).utc();

        const checkInDateTime = moment(
          new Date(agentExtensionLogsData.check_in_time),
        ).utc();
        const releaseDateTime = moment(
          new Date(agentExtensionLogsData.release_time),
        ).utc();
        const checkOutDateTime = moment(
          new Date(agentExtensionLogsData.check_out_time),
        ).utc();
        const checkOutDateTimeBefore10 = moment(
          new Date(agentExtensionLogsData.check_out_time),
        ).utc();

        checkOutDateTimeBefore10.subtract(10, 'minute');

        // case1
        if (
          currentDateTime > checkInDateTime &&
          currentDateTime > checkOutDateTime &&
          currentDateTime > releaseDateTime
        ) {
          logger.info('ExtensionDialog:', {
            action: 'get_agent_extension_logs',
            agent_extension: agentExtensionLogsData.extension,
            case: 'Case1',
            userEmail: userEmail,
            message: `Please enter your phone extension to start receiving calls. Because might be extension time out is passed`,
          });

          setShowExtensionDetails(extensionDetailsInput);
          setReason('Extension not received');
        }
        // case2
        else if (
          currentDateTime > checkInDateTime &&
          currentDateTime < checkOutDateTime &&
          currentDateTime < checkOutDateTimeBefore10 &&
          currentDateTime < releaseDateTime
        ) {
          const extension_details: AgentExtensionDialogDetails = {
            additionalInfo: (
              <p>
                Please review the extension assigned to you and update, if
                incorrect. Click Ok to continue.
              </p>
            ),
            title: { show: true, text: 'Agent Extension' },
            btnOk: { show: true, text: 'OK' },
            maximumWidth: 'xs',
            isChildComponent: true,
          };
          setShowExtensionDetails(extension_details);
          setProps({
            extension: agentExtensionLogsData.extension,
          });
          setData({
            extension: agentExtensionLogsData.extension,
            isvalid: agentExtensionLogsData.extension ? true : false,
          });
          withLocalStorage(
            STORAGE_KEY_AGENT_EXTENSION_FROM_API,
            agentExtensionLogsData ? agentExtensionLogsData.extension : '',
          );

          setAgentExtensionLogs(agentExtensionLogsData);
          setReason('Extension received');

          logger.info('ExtensionDialog:', {
            action: 'get_agent_extension_logs',
            agent_extension: agentExtensionLogsData.extension,
            case: 'Case2',
            userEmail: userEmail,
            message: `Please review the extension assigned to you and update. agent Extension successfully Retrieved and Display On Dialog Box`,
          });
        }
        // case 3
        else if (
          currentDateTime > checkInDateTime &&
          currentDateTime > checkOutDateTime &&
          currentDateTime < releaseDateTime &&
          !caseId
        ) {
          const extension_details: AgentExtensionDialogDetails = {
            additionalInfo: (
              <h4>
                Your extension check out time has passed. If you wish to extend
                your shift, please request that to your supervisor extend the
                checkout time and try logging into the CV portal again.
              </h4>
            ),
            title: { show: true, text: 'Agent Extension' },
            btnOk: { show: true, text: 'Logout' },
            maximumWidth: 'sm',
            isChildComponent: false,
          };
          setShowExtensionDetails(extension_details);
          setReason('Extension not received');

          logger.info('ExtensionDialog:', {
            action: 'get_agent_extension_logs',
            agent_extension: agentExtensionLogsData.extension,
            case: 'Case3',
            userEmail: userEmail,
            message: `Your extension check out time has passed.`,
          });
        } else {
          logger.info('ExtensionDialog:', {
            action: 'get_agent_extension_logs',
            agent_extension: agentExtensionLogsData.extension,
            case: 'NoCase',
            userEmail: userEmail,
            message: `Please enter your phone extension to start receiving calls. Because might be extension time out is passed`,
          });
          setShowExtensionDetails(extensionDetailsInput);
          setReason('Extension not received');
        }
      } else {
        logger.info('ExtensionDialog:', {
          action: 'get_agent_extension_logs',
          agent_extension: agentExtensionLogsData?.extension,
          case: 'NoCase',
          userEmail: userEmail,
          message: `Please enter your phone extension to start receiving calls. Because might be extension time out is passed`,
        });
        setShowExtensionDetails(extensionDetailsInput);
        setReason('Extension not received');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agentExtension,
    agentExtensionLogsData,
    caseId,
    isOpen,
    userEmail,
    withLocalStorage,
  ]);

  const handleClose = async (ok?: boolean): Promise<boolean> => {
    setFormSubmitted(true);

    if (data.isvalid && ok && data.extension) {
      const misMatchExtension =
        data.extension &&
        localStorageAgentExtension &&
        localStorageAgentExtension !== null &&
        data.extension !== localStorageAgentExtension
          ? 'The assigned extension does not match with the current extension. Because user has changed the extension manually'
          : '';

      const newReason = misMatchExtension ? 'Extension Modified' : reason;

      const loginData: LoginData = {
        email_address: userEmail,
        agent_extension: data.extension,
        extension_logs: agentExtensionLogs,
        reason: newReason,
      };

      logger.info('ExtensionDialog:', {
        action: 'SaveLoginData',
        agent_extension: data.extension,
        message: 'Login Details Received',
        userEmail: userEmail,
        loginData: loginData,
        reason: reason,
      });

      const res = await api.post_logindata(loginData);
      if (res?.agent_extension) {
        logger.info('ExtensionDialog:', {
          action: 'SaveLoginData',
          agent_extension: res?.agent_extension,
          message: 'Login Details Saved into DB.',
          userEmail: userEmail,
          reason: reason,
        });

        logger.info('ExtensionDialog:', {
          action: 'SaveLoginData',
          agent_extension_input: data.extension,
          message: misMatchExtension,
          userEmail: userEmail,
          agent_extension_from_client: localStorageAgentExtension,
          reason: reason,
        });

        setOpen(false);
        saveExtension(data.extension);
        if (closeExtensionDialog) {
          closeExtensionDialog();
        }
        return true;
      }
      return false;
    }
    return true;
  };

  const handleLogout = async (): Promise<boolean> => {
    setFormSubmitted(true);
    auth.signOut();
    setOpen(false);
    if (closeExtensionDialog) {
      closeExtensionDialog();
    }
    return true;
  };

  const validator = (valid: boolean, extnVal: string) => {
    setData({
      extension: extnVal,
      isvalid: valid,
    });
  };

  const tooltiplErrorMessage = () => {
    let message: string;
    if (!data.isvalid && !data.extension) {
      message = 'Extension is required';
    } else if (!data.isvalid && data.extension) {
      message = 'Check For errors';
    } else {
      message = '';
    }
    return message;
  };

  useEffect(() => {
    if (requestFor === 'E911CallNotCompleted') {
      setBtnOk({ show: true, text: 'OK' });
    } else if (showExtensionDetails.btnOk?.show) {
      setBtnOk(showExtensionDetails.btnOk);
    } else {
      setBtnOk({ show: false, text: '' });
    }
  }, [requestFor, showExtensionDetails.btnOk]);

  return (
    <StyledRoot>
      <CommonDialog
        customstyle={StyledPaper}
        maxWidth={
          showExtensionDetails.maximumWidth
            ? showExtensionDetails.maximumWidth
            : 'sm'
        }
        id="extension-dialog"
        keepMounted={keepMounted}
        open={open}
        onConfirm={
          showExtensionDetails.btnOk?.text === 'Logout'
            ? handleLogout
            : handleClose
        }
        title={
          showExtensionDetails.title
            ? showExtensionDetails.title
            : { show: true, text: 'Enter Extension' }
        }
        btnCancel={{ show: false, text: 'Cancel' }}
        btnOk={btnOkData}
        showErrorTooltipMessgae={
          showExtensionDetails.btnOk?.text === 'OK'
            ? tooltiplErrorMessage()
            : ''
        }
      >
        <StyledPhoneOuter>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            enableBackground="new 0 0 24 24"
            height="36px"
            viewBox="0 0 24 24"
            width="36px"
            fill="#FFFFFF"
          >
            <rect fill="none" height="24" width="24" />
            <g>
              <g>
                <path d="M4.78,15.27l2.54-0.29c0.61-0.07,1.21,0.14,1.64,0.57l1.84,1.84c2.83-1.44,5.15-3.75,6.59-6.59l-1.85-1.85 c-0.43-0.43-0.64-1.03-0.57-1.64l0.29-2.52c0.12-1.01,0.97-1.77,1.99-1.77h1.73c1.13,0,2.07,0.94,2,2.07 c-0.53,8.54-7.36,15.36-15.89,15.89c-1.13,0.07-2.07-0.87-2.07-2v-1.73C3.01,16.24,3.77,15.39,4.78,15.27z" />
              </g>
            </g>
          </svg>
        </StyledPhoneOuter>
        <StyledText>
          {children ? children : showExtensionDetails.additionalInfo}
        </StyledText>
        {!showExtensionDetails.isChildComponent && !requestFor ? (
          <></>
        ) : (
          <div
            className={`extnOuter ${formSubmitted ? 'formSubmitted' : ''} ${
              data.isvalid ? 'isvalid' : 'notvalid'
            }`}
          >
            <ExtensionControl
              validator={validator}
              extensionData={props}
              locationKey="extension-dialog"
            />
          </div>
        )}
      </CommonDialog>
    </StyledRoot>
  );
};
